import { supabase } from "../utils/supabaseClient";
import {
  GetAllBranchSalesPayload,
  GetAllBranchSalesResponse,
  BranchInsightsAction,
  BranchInsightsParams,
  BranchInsightsResponse,
  GetItemPerformancePayload,
  GetItemPerformanceResponse,
  getBranchStartDateResponse,
} from "../types/analytics";
import { Json } from "../types/supabase";

export const analyticsService = {
  /**
   * Fetches all sales insights for a specific branch including item sales, AI sales, and total branch performance metrics.
   * Supports single date queries (p_date) or date range queries (p_start_date, p_end_date).
   * Note: Date range is limited to 30 days max.
   */
  getAllBranchSales: async (
    payload: GetAllBranchSalesPayload
  ): Promise<GetAllBranchSalesResponse> => {
    const { data, error } = await supabase.rpc("get_all_branch_sales", {
        payload :{
      p_branch_id: payload.p_branch_id,
      p_item_id: payload.p_item_id ?? null,
      p_date: payload.p_date ?? null,
      p_start_date: payload.p_start_date ?? null,
      p_end_date: payload.p_end_date ?? null,
    }} as any);

    if (error) {
      throw new Error(error.message);
    }

    return data as unknown as GetAllBranchSalesResponse;
  },

  /**
   * Retrieves detailed item analytics: Top 3 items, paginated list, totals etc.
   */
  getBranchInsights: async (
    actions: BranchInsightsAction[],
    params: BranchInsightsParams[]
  ): Promise<BranchInsightsResponse[]> => {
    // The RPC signature is: branch_insights(action_array JSONB[], params_array JSONB[])
    // We need to pass arrays of objects.
    const { data, error } = await supabase.rpc("branch_insights", {
      action_array: actions as any,
      params_array: params as any,
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as unknown as BranchInsightsResponse[];
  },

  /**
   * Returns monthly performance data for one or multiple items.
   */
  getItemPerformance: async (
    payload: GetItemPerformancePayload
  ): Promise<GetItemPerformanceResponse> => {
    const { data, error } = await supabase.rpc("get_item_performance", {
      payload: payload as any,
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as unknown as GetItemPerformanceResponse;
  },

  getBranchStartDate: async (branchId: string): Promise<getBranchStartDateResponse | null> => {
    const { data, error } = await supabase.rpc("get_branch_creation_date", {
      payload: {"branch_id" : branchId} as Json,
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as getBranchStartDateResponse | null;
  },
};